/**
 * EmptyState for the My Accountant page.
 *
 * Minimal body content (the sticky breadcrumb + title + subtitle live in
 * MyAccountantHeader):
 *   1. my-accountant.svg illustration (6s floating animation)
 *   2. Two actions: Invite your accountant + Browse Novala-certified accountants
 *   3. Plain 2-column "What they can do / can't do" text (no icons, no cards)
 *   4. PreviouslyConnectedSection below when history exists (will move into
 *      the main list as "Removed" rows in a follow-up PR)
 */

import { useId } from "react";
import illustrationSrc from "../../../../assets/illustrations/my-accountant.svg";
import { PALETTE } from "../constants";
import { PreviouslyConnectedSection } from "./PreviouslyConnectedSection";
import type { PreviousAccountant } from "../myAccountantTypes";

interface EmptyStateProps {
  onInviteClick: () => void;
  onBrowseClick: () => void;
  previousAccountants?: PreviousAccountant[];
  onReinvite?: (prev: PreviousAccountant) => void;
  onForget?: (previousId: string) => void;
}

const CAN_ITEMS = [
  "View and edit transactions, categories, reconciliations",
  "Run and export all accounting reports (P&L, balance sheet, GST/HST)",
  "Prepare year-end books for your tax filing",
];

const CANT_ITEMS = [
  "Run payroll or change payroll settings",
  "Invite other users or change permissions",
  "Delete the company or change your Novala subscription",
];

export function EmptyState({
  onInviteClick,
  onBrowseClick,
  previousAccountants = [],
  onReinvite,
  onForget,
}: EmptyStateProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = useEmptyStateStyles(uid);
  const showHistory =
    previousAccountants.length > 0 && !!onReinvite && !!onForget;

  return (
    <>
      {styleTag}
      <div
        className={`es-${uid}-wrap`}
        role="region"
        aria-label="My accountant empty state"
      >
        <div className={`es-${uid}-illus`}>
          <img
            src={illustrationSrc}
            alt="Business owner and accountant collaborating around a laptop"
            className={`es-${uid}-illus-img`}
          />
        </div>

        <div className={`es-${uid}-actions`}>
          <button
            type="button"
            className={`es-${uid}-btn-dark`}
            onClick={onInviteClick}
          >
            Invite your accountant
          </button>
          <button
            type="button"
            className={`es-${uid}-btn-outline`}
            onClick={onBrowseClick}
          >
            Browse Novala-certified accountants
          </button>
        </div>

        <div className={`es-${uid}-access`}>
          <div className={`es-${uid}-col`}>
            <h4 className={`es-${uid}-col-h`}>What they can do</h4>
            <ul className={`es-${uid}-ul`}>
              {CAN_ITEMS.map((item) => (
                <li key={item} className={`es-${uid}-li`}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className={`es-${uid}-col`}>
            <h4 className={`es-${uid}-col-h`}>What they can't do</h4>
            <ul className={`es-${uid}-ul`}>
              {CANT_ITEMS.map((item) => (
                <li key={item} className={`es-${uid}-li`}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {showHistory && (
          <PreviouslyConnectedSection
            previousAccountants={previousAccountants}
            onReinvite={onReinvite!}
            onForget={onForget!}
          />
        )}
      </div>
    </>
  );
}

function useEmptyStateStyles(uid: string) {
  const css = `
    .es-${uid}-wrap {
      width: 100%;
      max-width: 1000px;
      padding: 20px 48px 60px;
    }

    .es-${uid}-illus {
      max-width: 420px;
      margin: 0 auto 24px;
      animation: es-${uid}-float 6s ease-in-out infinite;
      display: block;
    }
    .es-${uid}-illus-img {
      width: 100%;
      height: auto;
      display: block;
    }
    @keyframes es-${uid}-float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-8px); }
    }

    .es-${uid}-actions {
      display: flex;
      gap: 10px;
      justify-content: center;
      margin: 0 0 48px;
      flex-wrap: wrap;
    }
    .es-${uid}-btn-dark {
      background: ${PALETTE.ink};
      color: #ffffff;
      border: 0;
      padding: 11px 20px;
      border-radius: 10px;
      font-weight: 600;
      font-size: 13.5px;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      box-shadow: 0 2px 6px -1px rgba(14, 26, 26, 0.18);
      transition: transform 160ms, box-shadow 160ms;
    }
    .es-${uid}-btn-dark:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px -2px rgba(14, 26, 26, 0.28);
    }
    .es-${uid}-btn-outline {
      background: transparent;
      border: 1.5px solid ${PALETTE.ink};
      color: ${PALETTE.ink};
      padding: 10px 18px;
      border-radius: 10px;
      font-weight: 600;
      font-size: 13.5px;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      transition: background 160ms;
    }
    .es-${uid}-btn-outline:hover {
      background: ${PALETTE.lineSoft};
    }

    .es-${uid}-access {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      max-width: 760px;
      margin: 0 auto;
    }
    .es-${uid}-col-h {
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      font-weight: 700;
      color: ${PALETTE.ink3};
      margin: 0 0 12px;
    }
    .es-${uid}-ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .es-${uid}-li {
      font-size: 13px;
      color: ${PALETTE.ink};
      line-height: 1.6;
      padding: 6px 0;
    }

    @media (max-width: 900px) {
      .es-${uid}-wrap { padding: 16px 24px 40px; }
      .es-${uid}-access { grid-template-columns: 1fr; gap: 24px; }
      .es-${uid}-actions { flex-direction: column; }
      .es-${uid}-btn-dark, .es-${uid}-btn-outline { width: 100%; justify-content: center; }
    }

    @media (prefers-reduced-motion: reduce) {
      .es-${uid}-illus { animation: none !important; }
    }
  `;
  return <style>{css}</style>;
}